import { useState } from 'react'
import { Clock, Loader2, Pencil, UserPlus, Users, X } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { InviteUserSheet } from '@/components/custom/admin/invite-user-sheet'
import { EditPortcoUserSheet } from '@/components/custom/admin/edit-portco-user-sheet'
import { formatAllowedAppsSummary } from '@/components/custom/admin/user-app-access-fields'
import { isPortalOgsmEnabled } from '@/configs/feature-flags'
import { OGSM_ACCESS_COPY } from '@/features/ogsm/access'
import {
  usePortalUsers,
  usePortalInvitations,
  useChangePortalUserRole,
  useChangePortalUserAccess,
  useChangePortalUserOgsmAccess,
  useRemovePortalUser,
  useRevokePortalInvitation,
} from '@/hooks/use-portal'
import { selectUser, useUserStore } from '@/stores/user.store'
import { showSuccess, showError } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { PortalUser, PortalInvitation } from '@/services/portal.service'

const PORTCO_ROLE_LABELS: Record<string, string> = {
  PORTCO_ADMIN: 'Admin',
  PORTCO_STANDARD: 'Member',
}

function getPortcoRoleLabel(role: string) {
  return PORTCO_ROLE_LABELS[role] ?? role
}

export function PortalTeamTab() {
  const { data, isLoading, isError, refetch } = usePortalUsers()
  const { data: invitationsData, isLoading: invitationsLoading } =
    usePortalInvitations()
  const currentProfile = useUserStore(selectUser)
  const changeRole = useChangePortalUserRole()
  const changeAccess = useChangePortalUserAccess()
  const changeOgsmAccess = useChangePortalUserOgsmAccess()
  const ogsmEnabled = isPortalOgsmEnabled()
  const removeUser = useRemovePortalUser()
  const revokeInvitation = useRevokePortalInvitation()

  const [inviteOpen, setInviteOpen] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [revokeConfirm, setRevokeConfirm] = useState<PortalInvitation | null>(
    null
  )

  const users = data?.data ?? []
  const invitations = invitationsData?.data ?? []
  const editingUser = editingUserId
    ? (users.find((u) => u.id === editingUserId) ?? null)
    : null

  const handleRevokeInvitation = async (invitation: PortalInvitation) => {
    try {
      await revokeInvitation.mutateAsync(invitation.id)
      showSuccess(`Invitation to ${invitation.emailAddress} revoked`)
      setRevokeConfirm(null)
    } catch (error) {
      showError(error, 'Failed to revoke invitation')
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="size-5 animate-spin text-primary/20" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2">
        <Typography variant="body-sm" className="text-primary/70">
          Failed to load team members.
        </Typography>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="max-w-3xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="size-4 text-secondary" />
            <Typography
              variant="body-sm"
              className="font-medium uppercase tracking-wider text-secondary"
            >
              Team Members
            </Typography>
            <Typography variant="body-sm" className="text-secondary">
              {users.length}
            </Typography>
          </div>
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setInviteOpen(true)}
          >
            <UserPlus className="size-3.5" />
            Invite
          </Button>
        </div>

        <div className="flex flex-col">
          {users.length === 0 ? (
            <div className="flex h-24 items-center justify-center rounded-sm border border-primary/10">
              <Typography variant="body-sm" className="text-primary/70">
                No team members yet. Invite someone to get started.
              </Typography>
            </div>
          ) : (
            users.map((user, index) => (
              <UserRow
                key={user.id}
                user={user}
                isCurrentUser={user.email === currentProfile?.email}
                isLast={index === users.length - 1}
                showOgsmLevel={ogsmEnabled}
                onEdit={() => setEditingUserId(user.id)}
              />
            ))
          )}
        </div>

        {!invitationsLoading && invitations.length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2">
              <Clock className="size-3.5 text-primary/70" />
              <Typography
                variant="body-sm"
                className="font-medium uppercase tracking-wider text-primary/70"
              >
                Pending Invitations
              </Typography>
              <Typography variant="body-sm" className="text-primary/70">
                {invitations.length}
              </Typography>
            </div>
            <div className="flex flex-col">
              {invitations.map((invitation, index) => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  isLast={index === invitations.length - 1}
                  onRevoke={setRevokeConfirm}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <InviteUserSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        mode="portco-implicit"
        askOgsmAccess={ogsmEnabled}
      />

      {editingUser && (
        <EditPortcoUserSheet
          open
          onOpenChange={(open) => {
            if (!open) setEditingUserId(null)
          }}
          user={editingUser}
          isCurrentUser={editingUser.email === currentProfile?.email}
          onChangeRole={(role) =>
            changeRole.mutateAsync({ userId: editingUser.id, role })
          }
          onChangeAccess={(allowedApps) =>
            changeAccess.mutateAsync({ userId: editingUser.id, allowedApps })
          }
          onRemove={
            editingUser.email === currentProfile?.email
              ? undefined
              : () => removeUser.mutateAsync({ userId: editingUser.id })
          }
          ogsmAccess={ogsmEnabled ? editingUser.ogsmAccess : undefined}
          onChangeOgsmAccess={
            ogsmEnabled
              ? (ogsmAccess) =>
                  changeOgsmAccess.mutateAsync({
                    userId: editingUser.id,
                    ogsmAccess,
                  })
              : undefined
          }
        />
      )}

      <AlertDialog
        open={revokeConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeConfirm(null)
        }}
      >
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                <X className="size-3 text-destructive" />
              </div>
              Revoke invitation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revokeConfirm && (
                <>
                  The invitation to{' '}
                  <span className="font-medium text-primary">
                    {revokeConfirm.emailAddress}
                  </span>{' '}
                  will be cancelled. You can send a new invitation later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 text-xs">Keep</AlertDialogCancel>
            <AlertDialogAction
              className="h-8 bg-destructive text-xs text-white hover:bg-destructive/90"
              onClick={() => {
                if (revokeConfirm) void handleRevokeInvitation(revokeConfirm)
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function UserRow({
  user,
  isCurrentUser,
  isLast,
  showOgsmLevel,
  onEdit,
}: {
  user: PortalUser
  isCurrentUser: boolean
  isLast: boolean
  showOgsmLevel: boolean
  onEdit: () => void
}) {
  return (
    <div
      className={cn(
        'group grid items-center gap-6 px-3 py-4 transition-colors',
        'grid-cols-[minmax(0,1fr)_auto]',
        'hover:bg-primary/[0.015]',
        !isLast && 'border-b border-primary/5'
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Typography
            variant="body"
            className="truncate text-[15px] font-medium text-secondary"
          >
            {user.name}
          </Typography>
          {isCurrentUser && (
            <Typography variant="body-sm" className="shrink-0 text-primary/70">
              (you)
            </Typography>
          )}
        </div>
        <Typography
          variant="body-sm"
          className="mb-2 truncate text-[13px] text-secondary"
        >
          {user.email}
        </Typography>
        <div className="flex flex-wrap items-center text-[12.5px] text-primary/70">
          <span className="font-semibold text-primary">
            {getPortcoRoleLabel(user.role)}
          </span>
          <span className="px-2 text-primary/25">·</span>
          {showOgsmLevel && (
            <>
              <span>{OGSM_ACCESS_COPY.rowLabel(user.ogsmCanEdit)}</span>
              <span className="px-2 text-primary/25">·</span>
            </>
          )}
          <span>{formatAllowedAppsSummary(user.allowedApps)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-primary/15 bg-background px-3 py-1.5 text-[12px] font-medium text-primary/70 opacity-0 transition-all hover:border-secondary hover:text-secondary group-hover:opacity-100"
      >
        <Pencil className="size-3" />
        Edit
      </button>
    </div>
  )
}

function InvitationRow({
  invitation,
  isLast,
  onRevoke,
}: {
  invitation: PortalInvitation
  isLast: boolean
  onRevoke: (invitation: PortalInvitation) => void
}) {
  const sentDate = new Date(invitation.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })

  return (
    <div
      className={cn(
        'group grid items-center gap-3 px-1 py-2.5',
        'grid-cols-[minmax(0,1fr)_120px_auto]',
        !isLast && 'border-b border-primary/5'
      )}
    >
      <div className="min-w-0">
        <Typography variant="body" className="truncate text-primary/70">
          {invitation.emailAddress}
        </Typography>
      </div>
      <Typography variant="body-sm" className="text-primary/70">
        Sent {sentDate}
      </Typography>
      <button
        type="button"
        className="cursor-pointer rounded-md p-1 text-primary/20 opacity-0 transition-all hover:bg-destructive/5 hover:text-destructive group-hover:opacity-100"
        onClick={() => onRevoke(invitation)}
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}
